import {
  AVATAR_SM_WIDTH,
  getResizedImageUrl,
} from '@rolebase/shared/helpers/getResizedImageUrl'
import BrandLogo from '@/common/atoms/BrandLogo'
import MeetingEditModal from '@/meeting/modals/MeetingEditModal'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import useOrgAdmin from '@/member/hooks/useOrgAdmin'
import OrgSwitch from '@/org/components/OrgSwitch'
import { useNavigateOrg } from '@/org/hooks/useNavigateOrg'
import { useOrgContext } from '@/org/contexts/OrgContext'
import { usePathInOrg } from '@/org/hooks/usePathInOrg'
import usePlanModules from '@/org/hooks/usePlanModules'
import { Org_Module_Enum } from '@gql'
import SearchGlobalModal from '@/search/components/SearchGlobalModal'
import TaskModal from '@/task/modals/TaskModal'
import ThreadEditModal from '@/thread/modals/ThreadEditModal'
import { useAuth } from '@/user/hooks/useAuth'
import {
  Avatar,
  Box,
  Flex,
  IconButton,
  Link,
  Menu,
  MenuButton,
  Spacer,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import settings from 'src/settings'
import { useStoreState } from '@store/hooks'
import React, { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BackIcon,
  HelpIcon,
  MeetingsIcon,
  MenuIcon,
  NewsIcon,
  NextStepsNavIcon,
  OrgChartIcon,
  SettingsIcon,
  SidebarLeftIcon,
  TasksIcon,
  ThreadsIcon,
} from 'src/icons'
import { SidebarContext } from '../contexts/SidebarContext'
import HelpMenuList from './HelpMenuList'
import SidebarItem from './SidebarItem'
import SidebarItemLink from './SidebarItemLink'
import SidebarItemLocked from './SidebarItemLocked'
import SidebarLayout from './SidebarLayout'
import SidebarMeetings from './SidebarMeetings'
import SidebarSearchField from './SidebarSearchField'
import SidebarNextSteps from './SidebarNextSteps'
import SidebarTasks from './SidebarTasks'
import SidebarThreads from './SidebarThreads'
import SidebarTopIcon from './SidebarTopIcon'
import SidebarTopIconLink from './SidebarTopIconLink'
import UserSettingsMenuList from './UserSettingsMenuList'
import useOrgMember from '@/member/hooks/useOrgMember'
import OnboardingTodo from '@/onboarding/components/OnboardingTodo'

// Force reset with fast refresh
// @refresh reset

const logoContainerHeight = 65

export default function Sidebar() {
  const { t } = useTranslation()
  const { isAuthenticated, user } = useAuth()
  const { orgId } = useOrgContext()
  const orgLoading = useStoreState((state) => state.orgs.loading)
  const currentMember = useCurrentMember()
  const currentMeetingId = useStoreState(
    (state) => state.memberStatus.currentMeetingId
  )
  const navigate = useNavigateOrg()
  const isMember = useOrgMember()
  const isAdmin = useOrgAdmin()

  // What the organization's plan opens (ADM-D9). A disabled organization
  // closes everything, whatever its plan.
  const planModules = usePlanModules()
  // Collapsed bar: a module the plan closes keeps its icon, unlit and inert
  const lockedIconStyle = (module: Org_Module_Enum) =>
    planModules.isEnabled(module)
      ? {}
      : { opacity: 0.4, pointerEvents: 'none' as const }

  // User display name and avatar
  const displayName = currentMember?.name || user?.displayName || ''
  const avatarSrc = currentMember?.picture
    ? getResizedImageUrl(currentMember.picture, AVATAR_SM_WIDTH) || undefined
    : undefined

  // Get Sidebar context
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('SidebarContext not found in Sidebar')
  }
  const { isMobile, expand, minimize, height } = context

  // Links
  const rootPath = usePathInOrg('')

  // Modals
  const searchModal = useDisclosure()
  const meetingModal = useDisclosure()
  const threadModal = useDisclosure()
  const taskModal = useDisclosure()

  // Use Cmd+K or Cmd+P keys to open search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'p' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        searchModal.onOpen()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  if (!isAuthenticated) return null

  return (
    <SidebarLayout>
      <Flex
        h={`${height || logoContainerHeight}px`}
        pl={isMobile ? 0 : 2}
        pr={isMobile ? 2 : 5}
        align="center"
      >
        {isMobile ? (
          /* Mobile: top bar with logo and icons */
          <>
            <OrgSwitch flex={1} />

            {!expand.isOpen && orgId && (
              <>
                <SidebarTopIconLink to={`${rootPath}news`} icon={NewsIcon}>
                  {t('Sidebar.news')}
                </SidebarTopIconLink>

                <SidebarTopIconLink to={`${rootPath}roles`} icon={OrgChartIcon}>
                  {t('Sidebar.roles')}
                </SidebarTopIconLink>

                {/* Collapsed bar: same rules, plainer look, an unlit and
                    inert icon. `pointerEvents` does the lock, the opacity says
                    why. The entries stay: grey out, never hide (ADM-D9). */}
                <Box {...lockedIconStyle(Org_Module_Enum.Meetings)}>
                  <SidebarTopIconLink
                    to={`${rootPath}meetings?member=${currentMember?.id || ''}`}
                    icon={MeetingsIcon}
                    alert={!!currentMeetingId}
                  >
                    {t('Sidebar.meetings')}
                  </SidebarTopIconLink>
                </Box>

                <Box {...lockedIconStyle(Org_Module_Enum.Threads)}>
                  <SidebarTopIconLink
                    to={`${rootPath}threads?member=${currentMember?.id || ''}`}
                    icon={ThreadsIcon}
                  >
                    {t('Sidebar.threads')}
                  </SidebarTopIconLink>
                </Box>

                <Box {...lockedIconStyle(Org_Module_Enum.Tasks)}>
                  <SidebarTopIconLink
                    to={`${rootPath}tasks?member=${currentMember?.id || ''}`}
                    icon={TasksIcon}
                  >
                    {t('Sidebar.tasks')}
                  </SidebarTopIconLink>
                </Box>

                {/* No filter in the link (G3-D13): the page already opens on
                    "my roles + assigned to me". */}
                <Box {...lockedIconStyle(Org_Module_Enum.NextSteps)}>
                  <SidebarTopIconLink
                    to={`${rootPath}next-steps?member=${currentMember?.id || ''}`}
                    icon={NextStepsNavIcon as any}
                  >
                    {t('Sidebar.nextSteps')}
                  </SidebarTopIconLink>
                </Box>
              </>
            )}

            <SidebarTopIcon
              icon={MenuIcon}
              isActive={expand.isOpen}
              onClick={expand.onToggle}
            >
              {t('Sidebar.menu')}
            </SidebarTopIcon>
          </>
        ) : (
          <Flex w="100%" align="center" ml={3} role="group">
            <OrgSwitch />
            <Spacer />
            {!minimize.isOpen && (
              <Tooltip label={t('Sidebar.close')} placement="right">
                <IconButton
                  aria-label=""
                  variant="ghost"
                  icon={<SidebarLeftIcon size={20} />}
                  onClick={minimize.onToggle}
                />
              </Tooltip>
            )}
          </Flex>
        )}
      </Flex>

      {(!isMobile || expand.isOpen) && (
        /* Desktop: Sidebar content */
        <Flex
          flex={1}
          flexDirection="column"
          pl={{ base: 0, md: 4 }}
          pr={{ base: 1, md: 2 }}
          pt={{ base: 2, md: 3 }}
          pb={3}
          overflowY={{
            base: 'auto',
            md: 'hidden', // Hide scrollbar on desktop
          }}
          _hover={{
            // Show scrollbar on hover
            overflowY: 'auto',
          }}
        >
          {!orgId && !orgLoading && window.location.pathname !== '/' && (
            <SidebarItemLink to="/" icon={BackIcon}>
              {t('Sidebar.orgs')}
            </SidebarItemLink>
          )}

          {orgId && (
            <>
              <SidebarSearchField
                showShortcut={!isMobile}
                onClick={searchModal.onOpen}
              />

              <OnboardingTodo />

              <SidebarItemLink to={`${rootPath}news`} icon={NewsIcon}>
                {t('Sidebar.news')}
              </SidebarItemLink>

              <SidebarItemLink to={`${rootPath}roles`} icon={OrgChartIcon}>
                {t('Sidebar.roles')}
              </SidebarItemLink>

              {/* 🔑 The sub-lists (upcoming meetings, threads, tasks) go
                  with their entry: threads listed under a greyed-out entry
                  would announce closed what stays readable. */}
              {planModules.isEnabled(Org_Module_Enum.Meetings) ? (
                <>
                  <SidebarItemLink
                    to={`${rootPath}meetings?member=${currentMember?.id || ''}`}
                    icon={MeetingsIcon}
                    alert={!!currentMeetingId}
                    onAdd={isMember ? meetingModal.onOpen : undefined}
                  >
                    {t('Sidebar.meetings')}
                  </SidebarItemLink>

                  <SidebarMeetings max={3} />
                </>
              ) : (
                <SidebarItemLocked
                  icon={MeetingsIcon}
                  label={t('Sidebar.meetings')}
                  module={Org_Module_Enum.Meetings}
                  isOrgDisabled={planModules.isOrgDisabled}
                />
              )}

              {planModules.isEnabled(Org_Module_Enum.Threads) ? (
                <>
                  <SidebarItemLink
                    to={`${rootPath}threads?member=${currentMember?.id || ''}`}
                    icon={ThreadsIcon}
                    onAdd={isMember ? threadModal.onOpen : undefined}
                  >
                    {t('Sidebar.threads')}
                  </SidebarItemLink>

                  <SidebarThreads />
                </>
              ) : (
                <SidebarItemLocked
                  icon={ThreadsIcon}
                  label={t('Sidebar.threads')}
                  module={Org_Module_Enum.Threads}
                  isOrgDisabled={planModules.isOrgDisabled}
                />
              )}

              {planModules.isEnabled(Org_Module_Enum.Tasks) ? (
                <>
                  <SidebarItemLink
                    to={`${rootPath}tasks?member=${currentMember?.id || ''}`}
                    icon={TasksIcon}
                    onAdd={isMember ? taskModal.onOpen : undefined}
                  >
                    {t('Sidebar.tasks')}
                  </SidebarItemLink>

                  <SidebarTasks />
                </>
              ) : (
                <SidebarItemLocked
                  icon={TasksIcon}
                  label={t('Sidebar.tasks')}
                  module={Org_Module_Enum.Tasks}
                  isOrgDisabled={planModules.isOrgDisabled}
                />
              )}

              {/* No filter in the link (G3-D13): the page already opens on
                  "my roles + assigned to me". */}
              {planModules.isEnabled(Org_Module_Enum.NextSteps) ? (
                <>
                  <SidebarItemLink
                    to={`${rootPath}next-steps?member=${currentMember?.id || ''}`}
                    icon={NextStepsNavIcon as any}
                  >
                    {t('Sidebar.nextSteps')}
                  </SidebarItemLink>
                  <SidebarNextSteps />
                </>
              ) : (
                <SidebarItemLocked
                  icon={NextStepsNavIcon as any}
                  label={t('Sidebar.nextSteps')}
                  module={Org_Module_Enum.NextSteps}
                  isOrgDisabled={planModules.isOrgDisabled}
                />
              )}
            </>
          )}

          <Spacer />

          <SidebarItemLink
            to={
              isAdmin
                ? `${rootPath}settings/org`
                : `${rootPath}settings/credentials`
            }
            icon={SettingsIcon}
          >
            {t('Sidebar.settings')}
          </SidebarItemLink>

          <Menu placement="right-end">
            <MenuButton as={SidebarItem} icon={HelpIcon}>
              {t('Sidebar.help')}
            </MenuButton>
            <HelpMenuList />
          </Menu>

          <Menu>
            <MenuButton
              as={SidebarItem}
              iconNode={
                <Avatar
                  name={displayName}
                  src={avatarSrc}
                  size="sm"
                  w="24px"
                  h="24px"
                  ml={4}
                  mr={3}
                />
              }
            >
              {displayName}
            </MenuButton>
            <UserSettingsMenuList />
          </Menu>

          {/* The instance logo, at the very bottom (UDN03b-D1, then Yohan on
              15/08: at the top « ça descend trop le reste »). It says which
              tool you are in without costing a line to what you use.
              Desktop only: on mobile the top strip already carries the org
              switcher and the navigation icons. */}
          {!isMobile && (
            <Flex mt={6} px={2} pb={settings.sourceUrl ? 0 : 2}>
              <BrandLogo fullWidth />
            </Flex>
          )}

          {/* The source it derives from, credited where it stays visible (#83) */}
          {settings.sourceUrl && (
            <Link
              href={settings.sourceUrl}
              isExternal
              px={2}
              pt={1}
              pb={2}
              fontSize="xs"
              color="gray.500"
              _dark={{ color: 'whiteAlpha.600' }}
            >
              {t('Sidebar.poweredBy')} ↗
            </Link>
          )}
        </Flex>
      )}

      {searchModal.isOpen && (
        <SearchGlobalModal
          isOpen
          onClose={() => {
            searchModal.onClose()
            expand.onClose()
          }}
        />
      )}

      {meetingModal.isOpen && (
        <MeetingEditModal isOpen onClose={meetingModal.onClose} />
      )}

      {threadModal.isOpen && (
        <ThreadEditModal isOpen onClose={threadModal.onClose} />
      )}

      {taskModal.isOpen && (
        <TaskModal
          isOpen
          onCreate={(taskId) => navigate(`tasks/${taskId}`)}
          onClose={taskModal.onClose}
        />
      )}
    </SidebarLayout>
  )
}
